import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { CUSTOMER_MENU, filterGroupsByModules } from "../config/menu";
import { useApp } from "../context/AppContext";
import { useI18n } from "../i18n";
import { Avatar, Button } from "../components/ui";
import Icon from "../components/ui/Icon";
import BrandMark from "../components/BrandMark";
import { brandingOf } from "../config/branding";

// พอร์ทัลลูกค้า — เว็บเดสก์ท็อปเต็มรูปแบบ ใช้โครง sidebar เดียวกับฝั่งแอดมิน ไม่มีเวอร์ชันมือถือแยก
export default function CustomerLayout() {
  const { organization, enabledModules, profile, logout, isAdmin } = useApp();
  const { t, lang, setLang, pick } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [userMenu, setUserMenu] = useState(false);

  useEffect(() => {
    setOpen(false);
    setUserMenu(false);
  }, [location.pathname]);

  // White-label: ร้านที่มีสิทธิ์ใช้ชื่อ/โลโก้/สีของตัวเองแทน LevelUp
  const brand = useMemo(() => brandingOf(organization), [organization]);

  const groups = useMemo(() => filterGroupsByModules(CUSTOMER_MENU, enabledModules), [enabledModules]);

  const title = useMemo(() => {
    const all = CUSTOMER_MENU.flatMap((group) => group.items);
    const found = all
      .filter((item) => (item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to)))
      .sort((a, b) => b.to.length - a.to.length)[0];
    return found ? pick(found, "label") : organization?.name;
  }, [location.pathname, organization, pick]);

  const onLogout = async () => {
    await logout();
    navigate("/auth/login");
  };

  return (
    <div className="crm-shell" style={brand.cssVars}>
      <aside className={`crm-sidebar ${open ? "is-open" : ""}`}>
        <div className="crm-sidebar__brand">
          <BrandMark brand={brand} />
          <div>
            <div>{brand.name}</div>
            {brand.custom ? null : <div style={{ fontSize: 11, color: "#a5b4fc", fontWeight: 500 }}>{organization?.name}</div>}
          </div>
        </div>

        <div className="crm-sidebar__scroll">
          {groups.map((group) => (
            <div key={group.group}>
              <div className="crm-sidebar__group">{pick(group, "group")}</div>
              {group.items.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.exact} className={({ isActive }) => `crm-nav-link ${isActive ? "is-active" : ""}`}>
                  <span className="crm-nav-link__icon"><Icon name={item.icon} /></span>
                  {pick(item, "label")}
                </NavLink>
              ))}
            </div>
          ))}

          {isAdmin ? (
            <>
              <div className="crm-sidebar__group">{t("nav.settings")}</div>
              <Link to="/admin" className="crm-nav-link">
                <span className="crm-nav-link__icon"><Icon name="desktop" /></span>
                {lang === "en" ? "Go to admin" : "ไปหน้าผู้ดูแล"}
              </Link>
            </>
          ) : null}
        </div>
      </aside>

      {open ? <div className="crm-backdrop" onClick={() => setOpen(false)} /> : null}

      <div className="crm-main">
        <header className="crm-topbar">
          <Button className="crm-burger" size="sm" onClick={() => setOpen((v) => !v)}>
            <Icon name="menu" />
          </Button>
          <h2 className="crm-topbar__title">{title}</h2>

          <div className="crm-topbar__spacer" />

          <Button size="sm" onClick={() => setLang(lang === "th" ? "en" : "th")}>
            {lang === "th" ? "🇹🇭 ไทย" : "🇬🇧 EN"}
          </Button>

          <div style={{ position: "relative" }}>
            <button
              type="button"
              style={{ border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
              onClick={() => setUserMenu((v) => !v)}
            >
              <Avatar name={profile?.fullname || profile?.email} size="sm" />
            </button>
            {userMenu ? (
              <div
                style={{
                  position: "absolute", right: 0, top: 42, background: "#fff", border: "1px solid #e2e8f0",
                  borderRadius: 10, boxShadow: "0 18px 40px rgba(15,23,42,.16)", zIndex: 50, minWidth: 200, padding: 6,
                }}
              >
                <div style={{ padding: "8px 10px", fontSize: 13 }}>
                  <div className="u-strong">{profile?.fullname || profile?.email}</div>
                  <div className="u-fs-12 u-muted">{profile?.email}</div>
                </div>
                <Link to="/app/profile" style={{ display: "block", padding: "8px 10px", fontSize: 13, color: "#334155" }}>
                  {t("nav.settings")}
                </Link>
                <button
                  type="button"
                  style={{ display: "block", width: "100%", textAlign: "left", border: "none", background: "none", padding: "8px 10px", color: "#f43f5e", cursor: "pointer", fontSize: 13 }}
                  onClick={onLogout}
                >
                  {t("action.logout")}
                </button>
              </div>
            ) : null}
          </div>
        </header>

        <main className="crm-content">
          <div className="customer-content">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
